import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Users,
  Download,
  Search,
  LogOut,
  Flame,
  Trash2,
  RefreshCw,
  Loader2,
  IdCard,
  Trophy,
  ExternalLink,
  QrCode,
  MessageCircle,
  Mail,
  CopyCheck,
  Layers,
  Plus,
  Pencil,
  X,
} from "lucide-react";
import { waLink, mailtoLink } from "../visitor/followup";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
const RATING_META = {
  hot: { label: "Hot", cls: "bg-red-100 text-red-700 border-red-200" },
  warm: { label: "Warm", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  cold: { label: "Cold", cls: "bg-blue-100 text-blue-700 border-blue-200" },
};

const SOURCE_META = {
  badge_scan: { label: "Badge", cls: "bg-teal-100 text-teal-700 border-teal-200" },
  card_scan: { label: "Card", cls: "bg-violet-100 text-violet-700 border-violet-200" },
  manual: { label: "Manual", cls: "bg-stone-100 text-stone-600 border-stone-200" },
};

export default function VisitorsDashboard({ user, onLogout }) {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [rating, setRating] = useState("");
  const [source, setSource] = useState("");
  const [staff, setStaff] = useState("");
  const [country, setCountry] = useState("");
  const [city, setCity] = useState("");
  const [group, setGroup] = useState("");
  const [sort, setSort] = useState("recent");
  const [cardImg, setCardImg] = useState(null);
  const canViewAll = useMemo(
    () => stats?.can_view_all ?? (user?.permissions || []).includes("visitor.view_all"),
    [stats, user]
  );

  // Duplicate review
  const [dupOpen, setDupOpen] = useState(false);
  const [dupLoading, setDupLoading] = useState(false);
  const [dupGroups, setDupGroups] = useState([]);
  const [dupSel, setDupSel] = useState({}); // { id: true } → marked for deletion
  const [dupBusy, setDupBusy] = useState(false);
  const [groupsOpen, setGroupsOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [l, s] = await Promise.all([
        axios.get(`${API}/visitors`, {
          ...authHeaders(),
          params: { q, rating, source, captured_by: staff, country, city, country_group: group, sort },
        }).then((r) => r.data),
        axios.get(`${API}/visitors/stats`, {
          ...authHeaders(),
          params: { q, source, captured_by: staff, country, city, country_group: group },
        }).then((r) => r.data),
      ]);
      setItems(l.items || []);
      setStats(s);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load visitors");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, rating, source, staff, country, city, group, sort]);
  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/visitors/export.csv`, { ...authHeaders(), responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "exhibition_visitors.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  const viewCard = async (imageId) => {
    try {
      const res = await axios.get(`${API}/visitors/cards/${imageId}`, { ...authHeaders(), responseType: "blob" });
      setCardImg(URL.createObjectURL(res.data));
    } catch {
      toast.error("Could not load card image");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this visitor?")) return;
    try {
      await axios.delete(`${API}/visitors/${id}`, authHeaders());
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    }
  };

  const findDuplicates = async () => {
    setDupOpen(true);
    setDupLoading(true);
    try {
      const data = await axios.get(`${API}/visitors/duplicates`, authHeaders()).then((r) => r.data);
      const groups = data.groups || [];
      setDupGroups(groups);
      // Pre-select every non-keep member for deletion (user can untick).
      const sel = {};
      groups.forEach((g) => g.members.forEach((m) => { if (m.id !== g.keep_id) sel[m.id] = true; }));
      setDupSel(sel);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not scan for duplicates");
      setDupGroups([]);
    } finally {
      setDupLoading(false);
    }
  };

  const toggleDup = (id) => setDupSel((s) => ({ ...s, [id]: !s[id] }));

  const selectedDupIds = useMemo(
    () => Object.keys(dupSel).filter((id) => dupSel[id]),
    [dupSel]
  );

  const deleteSelectedDuplicates = async () => {
    if (!selectedDupIds.length) return;
    if (!window.confirm(`Delete ${selectedDupIds.length} selected duplicate lead(s)? This can be undone in the database.`)) return;
    setDupBusy(true);
    try {
      const res = await axios.post(`${API}/visitors/bulk-delete`, { ids: selectedDupIds }, authHeaders()).then((r) => r.data);
      toast.success(`Deleted ${res.deleted_count} duplicate lead(s)`);
      await findDuplicates();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete duplicates");
    } finally {
      setDupBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <Toaster richColors position="top-center" />
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b border-stone-200 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-teal-600 flex items-center justify-center">
            <Users className="text-white" size={22} />
          </div>
          <div>
            <h1 className="text-lg font-bold text-stone-900 leading-tight">Exhibition Visitors</h1>
            <p className="text-xs text-stone-500">Centralised lead capture dashboard</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} data-testid="vd-refresh">
            <RefreshCw size={15} className="mr-1" /> Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={findDuplicates} data-testid="vd-find-duplicates">
            <CopyCheck size={15} className="mr-1" /> Find duplicates
          </Button>
          <Button size="sm" className="bg-teal-600 hover:bg-teal-700" onClick={exportCsv} data-testid="vd-export">
            <Download size={15} className="mr-1" /> Export CSV
          </Button>
          <Button variant="ghost" size="sm" onClick={() => (onLogout ? onLogout() : nav("/login"))} data-testid="vd-logout">
            <LogOut size={15} />
          </Button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-6">
        {/* KPIs */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Kpi label="Total leads" value={stats.total} icon={<Users size={18} />} color="text-teal-600" />
            <Kpi label="Hot" value={stats.by_rating?.hot ?? 0} icon={<Flame size={18} />} color="text-red-600" />
            <Kpi label="Warm" value={stats.by_rating?.warm ?? 0} icon={<Flame size={18} />} color="text-amber-600" />
            <Kpi label="Cold" value={stats.by_rating?.cold ?? 0} icon={<Flame size={18} />} color="text-blue-600" />
          </div>
        )}

        {/* Captured via */}
        {stats?.by_source && (
          <div className="mb-6 bg-white border border-stone-200 rounded-xl p-4 flex flex-wrap items-center gap-3" data-testid="vd-by-source">
            <span className="text-xs font-semibold uppercase tracking-wide text-stone-500">Captured via</span>
            {["badge_scan", "card_scan", "manual"].map((s) => (
              <Badge key={s} variant="outline" className={SOURCE_META[s].cls} data-testid={`vd-src-${s}`}>
                {SOURCE_META[s].label} · {stats.by_source[s] ?? 0}
              </Badge>
            ))}
          </div>
        )}

        {/* Today's leaderboard + per-staff all-time */}
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          {stats?.leaderboard?.length > 0 && (
            <div className="bg-white border border-stone-200 rounded-xl p-4" data-testid="vd-leaderboard">
              <div className="flex items-center gap-2 mb-3">
                <Trophy size={16} className="text-amber-500" />
                <span className="text-xs font-semibold uppercase tracking-wide text-stone-500">
                  Today's Leaderboard · {stats.leaderboard_date} ({stats.today_total} leads)
                </span>
              </div>
              <div className="space-y-2">
                {stats.leaderboard.map((s, i) => (
                  <div key={s.captured_by_id} className="flex items-center justify-between" data-testid={`vd-lb-${s.captured_by_id}`}>
                    <div className="flex items-center gap-2.5">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        i === 0 ? "bg-amber-100 text-amber-700" : i === 1 ? "bg-stone-200 text-stone-600" : i === 2 ? "bg-orange-100 text-orange-700" : "bg-stone-100 text-stone-500"
                      }`}>{i + 1}</span>
                      <span className="text-sm text-stone-800">{s.name}</span>
                    </div>
                    <div className="flex items-center gap-2 flex-1 ml-3 max-w-[55%]">
                      <div className="flex-1 h-2 bg-stone-100 rounded-full overflow-hidden">
                        <div className="h-full bg-teal-500 rounded-full" style={{ width: `${Math.round((s.count / (stats.leaderboard[0].count || 1)) * 100)}%` }} />
                      </div>
                      <span className="text-sm font-bold text-teal-700 w-6 text-right">{s.count}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Per-staff all-time */}
          {stats?.per_staff?.length > 0 && (
            <div className="bg-white border border-stone-200 rounded-xl p-4">
              <div className="text-xs font-semibold uppercase tracking-wide text-stone-500 mb-3">Leads per staff · all-time</div>
              <div className="flex flex-wrap gap-2">
                {stats.per_staff.map((s) => (
                  <Badge key={s.captured_by_id} variant="secondary" className="text-sm" data-testid={`vd-staff-${s.captured_by_id}`}>
                    {s.name} · {s.count}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={16} className="absolute left-3 top-2.5 text-stone-400" />
            <Input className="pl-9" placeholder="Search name, company, email, phone…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="vd-search" />
          </div>
          <div className="flex gap-1">
            {["", "hot", "warm", "cold"].map((r) => (
              <Button
                key={r || "all"} size="sm"
                variant={rating === r ? "default" : "outline"}
                className={rating === r ? "bg-teal-600 hover:bg-teal-700" : ""}
                onClick={() => setRating(r)}
                data-testid={`vd-filter-${r || "all"}`}
              >
                {r ? RATING_META[r].label : "All"}
              </Button>
            ))}
          </div>

          <Select value={source || "all"} onValueChange={(v) => setSource(v === "all" ? "" : v)}>
            <SelectTrigger className="w-[140px]" data-testid="vd-source-filter"><SelectValue placeholder="Source" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sources</SelectItem>
              <SelectItem value="badge_scan">Badge</SelectItem>
              <SelectItem value="card_scan">Card</SelectItem>
              <SelectItem value="manual">Manual</SelectItem>
            </SelectContent>
          </Select>

          {stats?.country_groups?.length > 0 && (
            <Select value={group || "all"} onValueChange={(v) => { const nv = v === "all" ? "" : v; setGroup(nv); if (nv) setCountry(""); }}>
              <SelectTrigger className="w-[160px]" data-testid="vd-group-filter"><SelectValue placeholder="Group" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All groups</SelectItem>
                {stats.country_groups.map((g) => (
                  <SelectItem key={g.id} value={g.id}>{g.name} ({g.countries?.length || 0})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {canViewAll && (
            <Button variant="outline" size="sm" onClick={() => setGroupsOpen(true)} data-testid="vd-manage-groups">
              <Layers size={15} className="mr-1" /> Groups
            </Button>
          )}

          {stats?.countries?.length > 0 && (
            <Select value={country || "all"} onValueChange={(v) => { const nv = v === "all" ? "" : v; setCountry(nv); if (nv) setGroup(""); }}>
              <SelectTrigger className="w-[150px]" data-testid="vd-country-filter"><SelectValue placeholder="Country" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All countries</SelectItem>
                {stats.countries.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {stats?.cities?.length > 0 && (
            <Select value={city || "all"} onValueChange={(v) => setCity(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[150px]" data-testid="vd-city-filter"><SelectValue placeholder="City" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All cities</SelectItem>
                {stats.cities.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {canViewAll && stats?.per_staff?.length > 0 && (
            <Select value={staff || "all"} onValueChange={(v) => setStaff(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[170px]" data-testid="vd-staff-filter"><SelectValue placeholder="Staff" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All staff</SelectItem>
                {stats.per_staff.map((s) => (
                  <SelectItem key={s.captured_by_id} value={s.captured_by_id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="w-[160px]" data-testid="vd-sort"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="recent">Newest first</SelectItem>
              <SelectItem value="oldest">Oldest first</SelectItem>
              <SelectItem value="name">Name A–Z</SelectItem>
              <SelectItem value="company">Company A–Z</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone-100 text-stone-600">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Name</th>
                  <th className="text-left px-4 py-3 font-semibold">Company</th>
                  <th className="text-left px-4 py-3 font-semibold">Contact</th>
                  <th className="text-left px-4 py-3 font-semibold">Rating</th>
                  <th className="text-left px-4 py-3 font-semibold">Via</th>
                  <th className="text-left px-4 py-3 font-semibold">Captured by</th>
                  <th className="text-left px-4 py-3 font-semibold">When</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody data-testid="vd-table-body">
                {loading ? (
                  <tr><td colSpan={8} className="px-4 py-10 text-center"><Loader2 className="animate-spin mx-auto text-teal-600" /></td></tr>
                ) : items.length === 0 ? (
                  <tr><td colSpan={8} className="px-4 py-10 text-center text-stone-400" data-testid="vd-empty">No visitors captured yet.</td></tr>
                ) : (
                  items.map((it) => (
                    <tr key={it.id} className="border-t border-stone-100 hover:bg-stone-50" data-testid={`vd-row-${it.id}`}>
                      <td className="px-4 py-3 font-medium text-stone-900">
                        <div className="flex items-center gap-2">
                          {it.name}
                          {it.card_image_id && (
                            <button onClick={() => viewCard(it.card_image_id)} title="View card" data-testid={`vd-card-${it.id}`}>
                              <IdCard size={15} className="text-teal-600" />
                            </button>
                          )}
                        </div>
                        {it.designation && <div className="text-xs text-stone-400">{it.designation}</div>}
                      </td>
                      <td className="px-4 py-3 text-stone-700">{it.company || "—"}</td>
                      <td className="px-4 py-3 text-stone-700">
                        <div>{it.phone || ""}</div>
                        <div className="text-xs text-stone-400">{it.email || ""}</div>
                      </td>
                      <td className="px-4 py-3">
                        {it.rating ? (
                          <Badge variant="outline" className={RATING_META[it.rating]?.cls}>{RATING_META[it.rating]?.label}</Badge>
                        ) : <span className="text-stone-300">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <Badge variant="outline" className={(SOURCE_META[it.source] || SOURCE_META.manual).cls}>
                            {(SOURCE_META[it.source] || SOURCE_META.manual).label}
                          </Badge>
                          {it.pass_url && (
                            <a href={it.pass_url} target="_blank" rel="noreferrer" title="Open official pass" data-testid={`vd-pass-${it.id}`}>
                              <ExternalLink size={14} className="text-teal-600" />
                            </a>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-stone-600 text-xs">{it.captured_by_name}</td>
                      <td className="px-4 py-3 text-stone-500 text-xs">{(it.captured_at || "").slice(0, 10)}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-3">
                          {waLink(it) && (
                            <a href={waLink(it)} target="_blank" rel="noreferrer" title="WhatsApp follow-up" data-testid={`vd-wa-${it.id}`}>
                              <MessageCircle size={15} className="text-green-600 hover:text-green-700" />
                            </a>
                          )}
                          {mailtoLink(it) && (
                            <a href={mailtoLink(it)} title="Email follow-up" data-testid={`vd-mail-${it.id}`}>
                              <Mail size={15} className="text-stone-400 hover:text-teal-600" />
                            </a>
                          )}
                          <button onClick={() => del(it.id)} title="Delete" data-testid={`vd-del-${it.id}`}>
                            <Trash2 size={15} className="text-stone-400 hover:text-red-600" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        <p className="text-xs text-stone-400 mt-3">
          Showing {items.length} {canViewAll ? "visitor(s) across all staff" : "of your captured visitor(s)"}.
        </p>
      </div>

      <Dialog open={!!cardImg} onOpenChange={(o) => { if (!o) { setCardImg(null); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Business Card</DialogTitle></DialogHeader>
          {cardImg && <img src={cardImg} alt="business card" className="w-full rounded-lg border" />}
        </DialogContent>
      </Dialog>

      <Dialog open={dupOpen} onOpenChange={setDupOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="vd-dup-dialog">
          <DialogHeader>
            <DialogTitle>Review duplicate leads</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-stone-500 -mt-2">
            Leads matching on <strong>both email and phone</strong>. The oldest in each group is kept by default; ticked rows will be deleted (recoverable).
          </p>

          {dupLoading ? (
            <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-teal-600" /></div>
          ) : dupGroups.length === 0 ? (
            <div className="py-10 text-center text-stone-500 text-sm" data-testid="vd-dup-empty">
              🎉 No duplicate leads found.
            </div>
          ) : (
            <div className="space-y-4">
              {dupGroups.map((g, gi) => (
                <div key={`${g.key_email}-${gi}`} className="border border-stone-200 rounded-xl overflow-hidden" data-testid={`vd-dup-group-${gi}`}>
                  <div className="bg-stone-100 px-3 py-2 text-xs text-stone-600 flex justify-between">
                    <span className="truncate">{g.key_email} · {g.key_phone}</span>
                    <span className="font-semibold whitespace-nowrap ml-2">{g.count} copies</span>
                  </div>
                  <div className="divide-y divide-stone-100">
                    {g.members.map((m) => {
                      const isKeep = m.id === g.keep_id;
                      const checked = !!dupSel[m.id];
                      return (
                        <label
                          key={m.id}
                          className={`flex items-start gap-3 px-3 py-2 text-sm cursor-pointer ${checked ? "bg-red-50" : "hover:bg-stone-50"}`}
                          data-testid={`vd-dup-row-${m.id}`}
                        >
                          <input
                            type="checkbox"
                            className="mt-1"
                            checked={checked}
                            onChange={() => toggleDup(m.id)}
                            data-testid={`vd-dup-check-${m.id}`}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-stone-900">{m.name || "—"}</span>
                              {isKeep && <Badge className="bg-green-100 text-green-700 border-green-200 text-[10px]">Keep (oldest)</Badge>}
                              {m.rating && <span className="text-[10px] text-stone-400 uppercase">{m.rating}</span>}
                            </div>
                            <div className="text-xs text-stone-500 truncate">
                              {[m.company, m.designation].filter(Boolean).join(" · ")}
                            </div>
                            <div className="text-[11px] text-stone-400 mt-0.5">
                              {SOURCE_META[m.source]?.label || m.source || "—"}
                              {m.captured_by_name ? ` · by ${m.captured_by_name}` : ""}
                              {m.captured_at ? ` · ${String(m.captured_at).slice(0, 10)}` : ""}
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}

          {dupGroups.length > 0 && (
            <div className="flex items-center justify-between border-t border-stone-200 pt-3 mt-2">
              <span className="text-xs text-stone-500">{selectedDupIds.length} selected for deletion</span>
              <Button
                size="sm"
                className="bg-red-600 hover:bg-red-700"
                disabled={dupBusy || selectedDupIds.length === 0}
                onClick={deleteSelectedDuplicates}
                data-testid="vd-dup-delete-selected"
              >
                {dupBusy ? <Loader2 size={15} className="mr-1 animate-spin" /> : <Trash2 size={15} className="mr-1" />}
                Delete selected ({selectedDupIds.length})
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <CountryGroupsDialog
        open={groupsOpen}
        onClose={() => setGroupsOpen(false)}
        countries={stats?.countries || []}
        onChanged={load}
      />
    </div>
  );
}

function CountryGroupsDialog({ open, onClose, countries, onChanged }) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // {id} while creating/editing
  const [name, setName] = useState("");
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("");

  const resetForm = () => { setEditing(null); setName(""); setSel({}); setFilter(""); };

  const loadGroups = async () => {
    setLoading(true);
    try {
      const data = await axios.get(`${API}/visitors/country-groups`, authHeaders()).then((r) => r.data);
      setGroups(data.groups || []);
    } catch {
      toast.error("Could not load groups");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) { loadGroups(); resetForm(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const startNew = () => { setEditing({ id: null }); setName(""); setSel({}); setFilter(""); };
  const startEdit = (g) => {
    setEditing(g); setName(g.name); setFilter("");
    const s = {}; (g.countries || []).forEach((c) => { s[c] = true; }); setSel(s);
  };
  const toggle = (c) => setSel((s) => ({ ...s, [c]: !s[c] }));
  const selCountries = Object.keys(sel).filter((c) => sel[c]);

  const save = async () => {
    const nm = name.trim();
    if (!nm) { toast.error("Enter a group name"); return; }
    setBusy(true);
    try {
      const body = { name: nm, countries: selCountries };
      if (editing?.id) await axios.put(`${API}/visitors/country-groups/${editing.id}`, body, authHeaders());
      else await axios.post(`${API}/visitors/country-groups`, body, authHeaders());
      toast.success("Group saved");
      resetForm();
      await loadGroups();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save group");
    } finally {
      setBusy(false);
    }
  };

  const del = async (g) => {
    if (!window.confirm(`Delete group "${g.name}"?`)) return;
    try {
      await axios.delete(`${API}/visitors/country-groups/${g.id}`, authHeaders());
      toast.success("Group deleted");
      if (editing?.id === g.id) resetForm();
      await loadGroups();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete group");
    }
  };

  const shown = (countries || []).filter((c) => c.toLowerCase().includes(filter.toLowerCase()));

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="vd-groups-dialog">
        <DialogHeader><DialogTitle>Country groups</DialogTitle></DialogHeader>
        <p className="text-xs text-stone-500 -mt-2">Bundle countries into named regions (e.g. Gulf, Europe) to filter leads by region.</p>

        {editing ? (
          <div className="space-y-3">
            <Input placeholder="Group name (e.g. Gulf, Europe)" value={name} onChange={(e) => setName(e.target.value)} data-testid="vd-group-name" />
            <Input placeholder="Filter countries…" value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="vd-group-country-search" />
            <div className="border border-stone-200 rounded-lg max-h-64 overflow-y-auto divide-y divide-stone-100">
              {shown.length === 0 ? (
                <div className="p-4 text-sm text-stone-400">No countries available. Leads must have a country set first.</div>
              ) : shown.map((c) => (
                <label key={c} className={`flex items-center gap-2 px-3 py-2 text-sm cursor-pointer ${sel[c] ? "bg-teal-50" : "hover:bg-stone-50"}`} data-testid={`vd-group-country-${c}`}>
                  <input type="checkbox" checked={!!sel[c]} onChange={() => toggle(c)} />
                  <span>{c}</span>
                </label>
              ))}
            </div>
            <div className="flex items-center justify-between border-t border-stone-200 pt-3">
              <span className="text-xs text-stone-500">{selCountries.length} selected</span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={resetForm} data-testid="vd-group-cancel"><X size={15} className="mr-1" /> Cancel</Button>
                <Button size="sm" className="bg-teal-600 hover:bg-teal-700" disabled={busy} onClick={save} data-testid="vd-group-save">
                  {busy ? <Loader2 size={15} className="mr-1 animate-spin" /> : null} Save group
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <Button size="sm" className="bg-teal-600 hover:bg-teal-700" onClick={startNew} data-testid="vd-group-new">
              <Plus size={15} className="mr-1" /> New group
            </Button>
            {loading ? (
              <div className="py-8 flex justify-center"><Loader2 className="animate-spin text-teal-600" /></div>
            ) : groups.length === 0 ? (
              <div className="py-6 text-center text-sm text-stone-400" data-testid="vd-groups-empty">No groups yet. Create one to get started.</div>
            ) : (
              <div className="space-y-2">
                {groups.map((g) => (
                  <div key={g.id} className="border border-stone-200 rounded-lg p-3 flex items-start justify-between" data-testid={`vd-group-item-${g.id}`}>
                    <div className="min-w-0">
                      <div className="font-medium text-stone-900">{g.name} <span className="text-xs font-normal text-stone-400">· {(g.countries || []).length} countries</span></div>
                      <div className="text-xs text-stone-500 mt-0.5 truncate">{(g.countries || []).join(", ") || "No countries"}</div>
                    </div>
                    <div className="flex gap-3 ml-3 shrink-0">
                      <button onClick={() => startEdit(g)} title="Edit" data-testid={`vd-group-edit-${g.id}`}><Pencil size={15} className="text-stone-400 hover:text-teal-600" /></button>
                      <button onClick={() => del(g)} title="Delete" data-testid={`vd-group-del-${g.id}`}><Trash2 size={15} className="text-stone-400 hover:text-red-600" /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Kpi({ label, value, icon, color }) {
  return (
    <div className="bg-white border border-stone-200 rounded-xl p-4">
      <div className={`flex items-center gap-2 ${color}`}>{icon}<span className="text-2xl font-bold">{value}</span></div>
      <div className="text-xs text-stone-500 mt-1">{label}</div>
    </div>
  );
}
